<script setup lang="ts">
/*
  The frame for everything outside the Designer and the Operator: the landing page and
  the dataspace list at `/`, the account page.

  HABITUS HAS NO APP BAR, so the top bar is built here from tokens: a 56px surface
  band with a rule under it, sticky to the top. The Notitia lock-up on the left, and on
  the right the theme, then either the account menu or the way in. It used to be a
  Vuetify `v-app-bar` in the section's primary colour, with `v-main` reserving its
  height; there is nothing to reserve now, since the bar is in the flow.

  The theme choice is on its own button and ALSO under the avatar, as on the rail's
  user menu: a signed-out visitor has no avatar, and a signed-in one looks for it where
  every other setting is.
*/
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import {
	mdiAccountCircleOutline,
	mdiBookOpenPageVariantOutline,
	mdiEmailFastOutline,
	mdiLogoutVariant,
	mdiOfficeBuildingOutline,
	mdiPaletteOutline,
	mdiShieldCrownOutline,
} from "@mdi/js";
import { HabAvatar, HabBanner, HabButton, HabMenu } from "@livereader/habitus-vue";
import type { HabMenuItem } from "@livereader/habitus-vue";
import BrandMark from "@/shared/components/BrandMark.vue";
import CountBadge from "@/shared/components/CountBadge.vue";
import { useAppearanceMenu } from "@/shared/composables/useAppearanceMenu";
import { useAuth } from "@/shared/composables/useAuth";
import { useInvitations } from "@/shared/composables/useInvitations";
import { useSystemAccess } from "@/shared/composables/useSystemAccess";

const { t, te } = useI18n();
const router = useRouter();
const { user, isAuthenticated, login, logout, authError } = useAuth();
const access = useSystemAccess();
const { count: pendingInvitations } = useInvitations();
const { items: appearanceItems, icon: appearanceIcon } = useAppearanceMenu();

// Unknown codes fall back to the generic message so a stray ?auth_error= never renders a raw key.
const authErrorMessage = computed(() => {
	const code = authError.value;
	if (!code) return null;
	const key = `auth.error.${code}`;
	return te(key) ? t(key) : t("auth.error.generic");
});
const displayName = computed(() => user.value?.display_name || t("common.unknownUser"));

// The badge is decorative, so the trigger's own name carries the number.
const triggerLabel = computed(() =>
	pendingInvitations.value > 0
		? t(
				"nav.accountMenuPending",
				{ name: displayName.value, n: pendingInvitations.value },
				pendingInvitations.value,
			)
		: t("nav.accountMenu", { name: displayName.value }),
);

const userItems = computed<HabMenuItem[]>(() => [
	{
		label: t("nav.account"),
		icon: mdiAccountCircleOutline,
		onSelect: () => router.push("/account"),
	},
	// Only while there is something to answer: the badge on the avatar says so, and this is where it
	// leads. The same count as the account's Invitations section carries.
	...(pendingInvitations.value > 0
		? [
				{
					label: t("nav.invitations", { n: pendingInvitations.value }),
					icon: mdiEmailFastOutline,
					onSelect: () =>
						router.push({ path: "/account", query: { section: "invitations" } }),
				},
			]
		: []),
	{
		label: t("nav.departments"),
		icon: mdiOfficeBuildingOutline,
		onSelect: () => router.push("/departments"),
	},
	// Offered to whoever's system role opens any of it; the control center itself checks again.
	...(access.canOpenControlCenter.value
		? [
				{
					label: t("nav.control-center"),
					icon: mdiShieldCrownOutline,
					onSelect: () => router.push("/control-center"),
				},
			]
		: []),
	// The documentation site the server serves beside the app (docs/README.md), in a tab of its own
	// as Alternis opens its own: it is not a route of this app, and it is public.
	{
		label: t("nav.documentation"),
		icon: mdiBookOpenPageVariantOutline,
		onSelect: () => window.open("/docs/", "_blank", "noopener"),
	},
	{ label: t("preferences.appearance"), icon: mdiPaletteOutline, items: appearanceItems.value },
	{ divider: true },
	{ label: t("nav.signOut"), icon: mdiLogoutVariant, onSelect: logout },
]);
</script>

<template>
	<!--
		`data-habitus` on the root now that everything this layout frames is Habitus: it
		used to sit on the bar alone while the pages under it were still Vuetify, whose
		markup the subtree's reset would have reached.
	-->
	<div
		data-habitus
		class="section-landing flex min-h-screen flex-col bg-bg"
	>
		<header
			class="landing-bar sticky top-0 z-(--hab-z-sticky) flex h-14 shrink-0 items-center gap-3 border-b border-border-default bg-surface px-4"
		>
			<!--
				`data-hab-brand` is Habitus's contract for a brand lock-up that is a link:
				without it base.css's unlayered prose-link rule paints it in accent ink.
			-->
			<router-link
				to="/"
				data-hab-brand
				class="flex items-center rounded-hab-sm text-fg no-underline"
				:aria-label="$t('common.appName')"
			>
				<BrandMark
					:size="28"
					:animate="false"
					:wordmark="$t('common.appName')"
				/>
			</router-link>

			<div class="ml-auto flex items-center gap-2">
				<HabMenu :items="appearanceItems">
					<template #trigger>
						<HabButton
							icon-only
							variant="text"
							tone="neutral"
							:icon="appearanceIcon"
							:aria-label="$t('preferences.appearance')"
						/>
					</template>
				</HabMenu>

				<HabMenu
					v-if="isAuthenticated"
					:items="userItems"
				>
					<template #trigger>
						<button
							type="button"
							class="cursor-pointer rounded-pill"
							:aria-label="triggerLabel"
						>
							<CountBadge :count="pendingInvitations">
								<HabAvatar
									:name="displayName"
									:color="user?.color || undefined"
									decorative
								/>
							</CountBadge>
						</button>
					</template>
				</HabMenu>

				<template v-else>
					<HabButton
						variant="text"
						tone="neutral"
						@click="login()"
					>
						{{ $t("nav.signIn") }}
					</HabButton>
				</template>
			</div>
		</header>

		<main class="flex flex-1 flex-col">
			<HabBanner
				v-if="authErrorMessage"
				tone="error"
				role="alert"
				:dismiss-label="$t('common.actions.close')"
				@dismiss="authError = null"
			>
				{{ authErrorMessage }}
			</HabBanner>
			<router-view />
		</main>
	</div>
</template>

<style scoped>
/* The bar's height, for a page that fills the viewport below it (HomeView). */
.section-landing {
	--landing-bar-height: 3.5rem;
}
</style>
